import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import { injectIntl, FormattedMessage } from 'react-intl';
import { Row, Col, Select, Button, Icon } from 'antd';
import Enums from 'utils/EnumsManager';
import { Panel } from 'components/ui/index';
import {
  getUserFolderData,
  fetchNewTemplateData,
  fetchTemplateData,
  setPermissionSettingVisible,
  setPaginations,
} from '../../flow/action';
import {
  setNewTemplateFolder,
  setNewTemplateName,
  setNewTemplateApiName,
  setNewTemplateDescription,
  setNewTemplateContent,
  setNewTemplateCategory,
  setEditTemplateFolder,
  setEditTemplateName,
  setEditTemplateApiName,
  setEditTemplateDescription,
  setEditTemplateContent,
  setEditTemplateCategory
} from '../flow/action';
import { setSelectedFolder } from '../../emailTemplates/flow/folderFlow/action';
import { SelectComponent, InputComponent } from '../component/formController';
import TemplateContent from './templateContent';
import styles from '../emailTemplatesCreation.less';

const cx = classNames.bind(styles);

const { ObjectTypes, DefaultPageConfigs } = Enums;
const { Leads } = ObjectTypes;

const BasicInfo = ({
  fieldOption,
  disableApiInput,
  setTemplateFolder,
  setTemplateName,
  setTemplateApiName,
  setTemplateDescription,
  setTemplateCategory,
  editTemplate,
  userFolders,
  selectedFolder,
  formatMessage
}) => {
  const categories = Object.keys(fieldOption).map((name, index) => ({
    id: index,
    name
  }));
  return (
    <Fragment>
      <SelectComponent
        defaultValue={selectedFolder.name ? selectedFolder.name : ''}
        onChange={setTemplateFolder}
        items={userFolders}
        fixedID="TemplateInformation"
        label={formatMessage({ id: 'page.emailTemplates.folder' })}
        value={v => v.id}
      />
      <InputComponent
        value={editTemplate.name}
        onChange={setTemplateName}
        label={formatMessage({ id: 'page.emailTemplates.emailTemplatesName' })}
      />
      <InputComponent
        disableInput={disableApiInput}
        value={editTemplate.api_name}
        onChange={setTemplateApiName}
        label={formatMessage({ id: 'page.emailTemplates.emailTemplateApiName' })}
      />
      <InputComponent
        optional
        value={editTemplate.description}
        onChange={setTemplateDescription}
        label={formatMessage({ id: 'page.emailTemplates.newTemplateDescription' })}
      />
      <SelectComponent
        hint={formatMessage({ id: 'page.emailTemplates.selectCategoryHint' })}
        defaultValue={editTemplate.category ? editTemplate.category : 'leads'}
        onChange={setTemplateCategory}
        items={categories}
        fixedID="TemplateInformation"
        label={formatMessage({ id: 'page.emailTemplates.category' })}
        value={v => v.name}
      />
    </Fragment>
  );
};

const FieldInfo = ({
  selectField, selectedValue, template, fieldOption, formatMessage
}) => (
  <Row className={`pt-lg ${cx(['new-template-input-row', 'new-template-folder-information'])}`}>
    <Col className="gutter-row field-value" span={10}>
      <div>
        {formatMessage({ id: 'page.emailTemplates.selectField' })}
      </div>
      <Select onChange={selectField} className="full-width" getPopupContainer={() => document.getElementById('TemplateInformation')}>
        { fieldOption[template.category] && fieldOption[template.category].map((item, index) =>
          <Select.Option key={item.id ? item.id : index} value={item.field_label}>{item.field_label}</Select.Option>)}
      </Select>
    </Col>
    <Col className="gutter-row field-value" offset={2} span={10}>
      <div>{formatMessage({ id: 'page.emailTemplates.fieldValue' })}</div>
      <div>{selectedValue}</div>
    </Col>
  </Row>
);

const ActionButtonGroup = ({ save, cancel, formatMessage }) => (
  <div className="pt-md pl-md pb-md">
    <Button className="email-theme-btn mr-md" onClick={save}>
      <Icon type="save" />
      { formatMessage({ id: 'page.emailTemplates.save' }) }
    </Button>
    <Button onClick={cancel}>
      <Icon type="close" />
      { formatMessage({ id: 'page.emailTemplates.cancel' }) }
    </Button>
  </div>
);

class TemplateInformation extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      defaultFolderName: '',
      selectedField: {},
      selectedLabel: '',
      selectedValue: ''
    };
  }

  componentDidMount() {
    const {
      editId,
      isNewTemplateRouter,
      loginUser,
      selectedFolder,
      templatesDataTablePagination,
      dispatchGetUserFolderData,
      dispatchSetNewTemplateFolder,
      dispatchSetNewTemplateCategory,
      dispatchFetchNewTemplateData,
      dispatchSetPermissionSettingVisible,
      dispatchFetchTemplateData,
      dispatchSetSelectedFolder,
    } = this.props;

    // fetch new template and edit template again to prevent the data lost on refresh
    dispatchFetchNewTemplateData(() => dispatchSetPermissionSettingVisible(false));
    dispatchFetchTemplateData({
      templateId: editId,
      cb: null
    });

    // Reloading aims to deal with csp issue for ckeditor
    // Keep folder id and current page params in url since reload will clear Redux data
    if (!window.location.hash) {
      const url = new URL(window.location.href);
      url.searchParams.append('id', selectedFolder.id);
      url.searchParams.append('page', templatesDataTablePagination.currentPage);
      url.searchParams.append('total', templatesDataTablePagination.total);
      url.hash = 'reloaded';

      window.location.replace(url.href);
    }

    const currentUrl = new URL(window.location.href);
    const folderIdParam = Number(currentUrl.searchParams.get('id'));

    dispatchGetUserFolderData(loginUser.id, () => {
      dispatchSetSelectedFolder({ id: folderIdParam });
    });

    if (isNewTemplateRouter()) {
      if (selectedFolder.id) {
        dispatchSetNewTemplateFolder(selectedFolder.id);
      }
      dispatchSetNewTemplateCategory(Leads);
    }
  }

    setTemplateCategory = (value) => {
      const {
        isNewTemplateRouter,
        dispatchSetNewTemplateCategory,
        dispatchSetEditTemplateCategory,
        dispatchSetNewTemplateContent,
        dispatchSetEditTemplateContent
      } = this.props;
      if (isNewTemplateRouter()) {
        dispatchSetNewTemplateContent('');
        this.setState({ selectedValue: 'removeAll' });
        dispatchSetNewTemplateCategory(value);
      } else {
        dispatchSetEditTemplateContent('');
        this.setState({ selectedValue: 'removeAll' });
        dispatchSetEditTemplateCategory(value);
      }
    }

    selectField = (value) => {
      const { newTemplate, fieldOption, editTemplate } = this.props;
      const setTemplate = !_.isEmpty(newTemplate) ? newTemplate : editTemplate;
      { !_.isEmpty(setTemplate) && _.isArray(fieldOption[setTemplate.category]) && fieldOption[setTemplate.category].forEach((item) => {
        if (value === item.field_label) {
          this.setState({
            selectedField: item,
            selectedValue: item.field_value
          });
        }
      }); }
    }

    render() {
      const { formatMessage } = this.props.intl;
      const {
        cancel,
        editTemplate,
        fieldOption,
        isNewTemplateRouter,
        newTemplate,
        onFileUpload,
        registerGetContentHook,
        selectedFolder,
        save,
        userFolders,
        dispatchSetNewTemplateFolder,
        dispatchSetNewTemplateName,
        dispatchSetNewTemplateApiName,
        dispatchSetNewTemplateDescription,
        dispatchSetNewTemplateContent,
        dispatchSetEditTemplateFolder,
        dispatchSetEditTemplateName,
        dispatchSetEditTemplateApiName,
        dispatchSetEditTemplateDescription,
        dispatchSetEditTemplateContent,
        dispatchSetPaginations,
      } = this.props;

      const currentUrl = new URL(window.location.href);
      const currentPageParam = Number(currentUrl.searchParams.get('page'));
      const totalParam = Number(currentUrl.searchParams.get('total'));
      const lastPage = Math.ceil((totalParam + 1) / DefaultPageConfigs.PageSize);
      const currentPage = isNewTemplateRouter() ? lastPage : currentPageParam;

      const handleCancel = () => {
        dispatchSetPaginations(DefaultPageConfigs.PageSize, currentPageParam, totalParam);
        cancel();
      };

      const handleSave = () => {
        dispatchSetPaginations(DefaultPageConfigs.PageSize, currentPage, totalParam);
        save();
      };

      return (
        <Panel
          panelTitle={<FormattedMessage id="page.emailTemplates.newEmailTemplate" />}
          panelClasses="email-theme-panel"
          contentClasses={`pl-lg pr-lg pt-lg pb-lg ${cx('new-template-panel-content')}`}
        >
          {isNewTemplateRouter() &&
            <Fragment>
              <BasicInfo
                fieldOption={fieldOption}
                disableApiInput={false}
                defaultFolderName={this.state.defaultFolderName}
                setTemplateFolder={dispatchSetNewTemplateFolder}
                setTemplateName={dispatchSetNewTemplateName}
                setTemplateApiName={dispatchSetNewTemplateApiName}
                setTemplateDescription={dispatchSetNewTemplateDescription}
                setTemplateCategory={this.setTemplateCategory}
                editTemplate={{}}
                userFolders={userFolders}
                selectedFolder={selectedFolder}
                formatMessage={formatMessage}
              />
              <FieldInfo
                selectedLabel={this.state.selectedLabel}
                selectedValue={this.state.selectedValue}
                selectedField={this.state.selectedField}
                selectField={this.selectField}
                template={newTemplate}
                fieldOption={fieldOption}
                formatMessage={formatMessage}
              />
              <TemplateContent
                registerGetContentHook={registerGetContentHook}
                setTemplateContent={dispatchSetNewTemplateContent}
                content={newTemplate.content}
                selectedValue={this.state.selectedValue}
                attachments={[]}
                onFileUpload={onFileUpload}
              />
            </Fragment>
          }
          {!isNewTemplateRouter() && !_.isEmpty(editTemplate) &&
            <Fragment>
              <BasicInfo
                fieldOption={fieldOption}
                disableApiInput
                defaultFolderName={this.state.defaultFolderName}
                setTemplateFolder={dispatchSetEditTemplateFolder}
                setTemplateName={dispatchSetEditTemplateName}
                setTemplateApiName={dispatchSetEditTemplateApiName}
                setTemplateDescription={dispatchSetEditTemplateDescription}
                setTemplateCategory={this.setTemplateCategory}
                editTemplate={editTemplate}
                userFolders={userFolders}
                selectedFolder={selectedFolder}
                formatMessage={formatMessage}
              />
              <FieldInfo
                selectedLabel={this.state.selectedLabel}
                selectedValue={this.state.selectedValue}
                selectedField={this.state.selectedField}
                selectField={this.selectField}
                template={editTemplate}
                fieldOption={fieldOption}
                formatMessage={formatMessage}
              />
              <TemplateContent
                registerGetContentHook={registerGetContentHook}
                setTemplateContent={dispatchSetEditTemplateContent}
                content={editTemplate.content}
                attachments={editTemplate.attachments}
                selectedValue={this.state.selectedValue}
                onFileUpload={onFileUpload}
              />
            </Fragment>
          }
          <ActionButtonGroup cancel={handleCancel} save={handleSave} formatMessage={formatMessage} />
        </Panel>

      );
    }
}

const mapStateToProps = ({ setup, loginUser }) => {
  const { emailTemplates } = setup;
  return {
    fieldOption: emailTemplates.fieldOption,
    loginUser,
    userFolders: emailTemplates.userFolders,
    selectedFolder: emailTemplates.selectedFolder,
    newTemplate: emailTemplates.newTemplate,
    editTemplate: emailTemplates.editTemplate,
    templatesDataTablePagination: emailTemplates.templatesDataTablePagination,
  };
};

const mapDispatchToProps = {
  dispatchGetUserFolderData: getUserFolderData,
  dispatchSetNewTemplateFolder: setNewTemplateFolder,
  dispatchSetNewTemplateName: setNewTemplateName,
  dispatchSetNewTemplateApiName: setNewTemplateApiName,
  dispatchSetNewTemplateDescription: setNewTemplateDescription,
  dispatchSetNewTemplateContent: setNewTemplateContent,
  dispatchSetNewTemplateCategory: setNewTemplateCategory,
  dispatchSetEditTemplateFolder: setEditTemplateFolder,
  dispatchSetEditTemplateName: setEditTemplateName,
  dispatchSetEditTemplateApiName: setEditTemplateApiName,
  dispatchSetEditTemplateDescription: setEditTemplateDescription,
  dispatchSetEditTemplateContent: setEditTemplateContent,
  dispatchSetEditTemplateCategory: setEditTemplateCategory,
  dispatchSetPermissionSettingVisible: setPermissionSettingVisible,
  dispatchFetchNewTemplateData: fetchNewTemplateData,
  dispatchFetchTemplateData: fetchTemplateData,
  dispatchSetSelectedFolder: setSelectedFolder,
  dispatchSetPaginations: setPaginations,
};


TemplateInformation.defaultProps = {
  editTemplate: {},
  newTemplate: {}
};

TemplateInformation.propTypes = {
  isNewTemplateRouter: PropTypes.func.isRequired,
  save: PropTypes.func.isRequired,
  cancel: PropTypes.func.isRequired,
  registerGetContentHook: PropTypes.func.isRequired,
};

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(TemplateInformation));



// WEBPACK FOOTER //
// ./src/views/email/emailTemplatesCreation/container/templateInformation.js